export interface SubtitleDescriptor {
  language: string;
  displayName: string;
  vttKey: string;
}

export type SubtitleTrackMode = "disabled" | "showing";

export interface SubtitleTrackNode {
  language: string;
  label: string;
  src: string;
  mode: SubtitleTrackMode;
}

export interface SubtitleTrackHost {
  removeSubtitleTracks(): void;
  createSubtitleTrack(track: Omit<SubtitleTrackNode, "mode">): SubtitleTrackNode;
  appendSubtitleTrack(track: SubtitleTrackNode): void;
  addLoadedMetadataListener(listener: () => void): void;
  removeLoadedMetadataListener(listener: () => void): void;
  isMetadataLoaded(): boolean;
}

const pendingMetadataListeners = new WeakMap<SubtitleTrackHost, () => void>();

export function preferredSubtitle(tracks: readonly Pick<SubtitleDescriptor, "language">[]): string {
  return tracks.some(track => track.language === "vi-VN") ? "vi-VN" : "";
}

export function selectSubtitle(
  tracks: readonly Pick<SubtitleDescriptor, "language">[],
  selectedLanguage: string,
  resetForEpisode: boolean,
): string {
  if (resetForEpisode) return preferredSubtitle(tracks);
  return selectedLanguage === "" || tracks.some(track => track.language === selectedLanguage)
    ? selectedLanguage
    : preferredSubtitle(tracks);
}

export function replaceSubtitleTracks(
  video: SubtitleTrackHost,
  tracks: readonly SubtitleDescriptor[],
  selectedLanguage: string,
): void {
  const preparedTracks = tracks.map(track => ({
    language: track.language,
    label: track.displayName,
    src: subtitleMediaUrl(track.vttKey),
  }));
  pendingMetadataListeners.get(video)?.();
  video.removeSubtitleTracks();
  const nodes = preparedTracks.map(track => video.createSubtitleTrack(track));
  for (const node of nodes) video.appendSubtitleTrack(node);
  if (nodes.length === 0) return;

  let listening = false;
  const listener = (): void => {
    if (pendingMetadataListeners.get(video) !== cancel) return;
    for (const node of nodes) node.mode = node.language === selectedLanguage ? "showing" : "disabled";
    cancel();
  };
  const cancel = (): void => {
    if (listening) video.removeLoadedMetadataListener(listener);
    if (pendingMetadataListeners.get(video) === cancel) pendingMetadataListeners.delete(video);
    listening = false;
  };

  pendingMetadataListeners.set(video, cancel);
  video.addLoadedMetadataListener(listener);
  listening = true;
  if (video.isMetadataLoaded()) listener();
}

export function browserSubtitleTrackHost(video: HTMLVideoElement): SubtitleTrackHost {
  return {
    removeSubtitleTracks: () => {
      for (const track of video.querySelectorAll("track[data-preview-subtitle]")) track.remove();
    },
    createSubtitleTrack: ({ language, label, src }) => ({ language, label, src, mode: "disabled" }),
    appendSubtitleTrack: track => {
      const element = document.createElement("track");
      element.dataset.previewSubtitle = "true";
      element.kind = "subtitles";
      element.srclang = track.language;
      element.label = track.label;
      element.src = track.src;
      element.track.mode = track.mode;
      video.append(element);
      Object.defineProperty(track, "mode", {
        get: () => element.track.mode === "showing" ? "showing" : "disabled",
        set: (mode: SubtitleTrackMode) => { element.track.mode = mode; },
        configurable: true,
      });
    },
    addLoadedMetadataListener: listener => { video.addEventListener("loadedmetadata", listener); },
    removeLoadedMetadataListener: listener => { video.removeEventListener("loadedmetadata", listener); },
    isMetadataLoaded: () => video.readyState >= HTMLMediaElement.HAVE_METADATA,
  };
}

function subtitleMediaUrl(vttKey: string): string {
  const segments = typeof vttKey === "string" ? vttKey.split("/") : [];
  if (segments.length === 0 || segments.some(segment => segment === "" || segment === "." || segment === "..")) {
    throw new Error("Invalid subtitle media key");
  }
  return `/media/${segments.map(segment => encodeURIComponent(segment)).join("/")}`;
}
